/**
 * Shipping Property Sync v4
 * -------------------------
 * Ensures every cart line-item carries the "Verwachte levering" property,
 * even when the item was added via bundle JS or upsell.
 *
 * v3: manually re-fetches and replaces cart drawer content after sync
 *     instead of relying on cart:refresh (which can miss on first add).
 *
 * v4 (14 SEP 2026) - fixes "geen geldige ID of regelnummer" on remove / quantity.
 *     Updating a line-item property makes Shopify re-key that line: the value in
 *     `data-line-key` (and in the `?line=` remove link) that was rendered server
 *     side no longer exists in the cart. Every later /cart/change.js call for that
 *     line then returns 422 and the theme prints Shopify's error under the item.
 *     v3 only re-rendered the cart DRAWER, and only after the whole loop, so the
 *     cart page and an already-open drawer kept the dead keys.
 *
 *     v4 therefore:
 *       1. re-keys the DOM immediately after every successful patch (data-line-key,
 *          the <line-item line-key> attribute and the remove links), on the cart
 *          page as well as in the drawer;
 *       2. never patches while a customer-initiated quantity/remove change is in
 *          flight, and re-runs afterwards;
 *       3. walks the cart by line KEY instead of by array index, so a line that
 *          moves or merges during the loop cannot be skipped or patched twice.
 */
(function () {
  'use strict';

  var ALL_DELIVERY_KEYS = ['Verwachte levering', 'Expected delivery', 'Voraussichtliche Lieferung', 'Livraison estimée'];
  var SECTION_ID   = 'variant-shipping-api';
  var LOCALE       = document.documentElement.lang || 'nl';
  var lang = LOCALE.substring(0, 2);
  var PROPERTY_KEY = lang === 'de' ? 'Voraussichtliche Lieferung' : lang === 'en' ? 'Expected delivery' : lang === 'fr' ? 'Livraison estimée' : 'Verwachte levering';

  var _isSyncing   = false;
  var _pendingSync = false;
  var _userBusy    = false;
  var _cache       = {};

  /* -- Helpers -- */

  function buildShippingMessage(customTime) {
    if (!customTime) return null;
    if (lang === 'de') return 'Bestellt vor 22:00 Uhr, geliefert in ' + customTime;
    if (lang === 'en') return 'Ordered before 22:00, delivered on ' + customTime;
    if (lang === 'fr') {
      var prepFr = customTime.toLowerCase().indexOf('weken') !== -1 ? 'sous' : 'le';
      return 'Commandé avant 22h00, livré ' + prepFr + ' ' + customTime;
    }
    var prep = customTime.toLowerCase().indexOf('weken') !== -1 ? 'in' : 'op';
    return 'Voor 22:00 besteld, geleverd ' + prep + ' ' + customTime;
  }

  function fetchVariantShipping(handle) {
    if (_cache[handle]) return Promise.resolve(_cache[handle]);
    var url = Shopify.routes.root + 'products/' + handle + '?section_id=' + SECTION_ID;
    return fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var el  = doc.querySelector('[data-variant-shipping]');
        if (!el) { _cache[handle] = { variants: {}, hide: false }; return _cache[handle]; }
        var data = JSON.parse(el.textContent);
        _cache[handle] = { variants: data.variants || {}, hide: data.hideDelivery === true };
        return _cache[handle];
      })
      .catch(function () { _cache[handle] = { variants: {}, hide: false }; return _cache[handle]; });
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  }

  /**
   * Point every reference to `oldKey` in the current DOM at `newKey`.
   * Without this the page keeps a line key that no longer exists in the cart and
   * the next remove / quantity change fails with "geen geldige ID of regelnummer".
   */
  function rekeyDom(oldKey, newKey) {
    if (!oldKey || !newKey || oldKey === newKey) return;

    var sel = '[data-line-key="' + cssEscape(oldKey) + '"]';
    Array.prototype.forEach.call(document.querySelectorAll(sel), function (el) {
      el.setAttribute('data-line-key', newKey);
    });

    var sel2 = '[line-key="' + cssEscape(oldKey) + '"]';
    Array.prototype.forEach.call(document.querySelectorAll(sel2), function (el) {
      el.setAttribute('line-key', newKey);
    });
  }

  /**
   * Rewrite every remove link to the key its own quantity input carries. The
   * server-rendered href uses a line NUMBER, which shifts as soon as a line is
   * re-keyed and moved.
   */
  function syncRemoveLinks() {
    Array.prototype.forEach.call(document.querySelectorAll('line-item-quantity'), function (wrapper) {
      var input = wrapper.querySelector('input[data-line-key]');
      var link  = wrapper.querySelector('a[href*="/cart/change"]');
      if (!input || !link) return;
      var key = input.getAttribute('data-line-key');
      if (!key) return;
      link.setAttribute('href', Shopify.routes.root + 'cart/change?id=' + encodeURIComponent(key) + '&quantity=0');
    });
  }

  /**
   * After properties have been patched server-side, re-fetch the cart-drawer
   * section HTML and swap it in. This is more reliable than dispatching
   * cart:refresh because we control the fetch (with a cache-buster) and
   * the DOM replacement directly.
   */
  async function refreshCartDrawer() {
    try {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer) return;

      var sectionEl = drawer.closest('.shopify-section');
      if (!sectionEl) return;

      var sectionId = sectionEl.id.replace('shopify-section-', '');
      var url = Shopify.routes.root + '?section_id=' + sectionId + '&_t=' + Date.now();

      var resp = await fetch(url);
      var html = await resp.text();

      var tmp = document.createElement('div');
      tmp.innerHTML = html;

      var freshDrawer = tmp.querySelector('#cart-drawer') || tmp.querySelector('cart-drawer');
      if (freshDrawer) {
        drawer.replaceChildren.apply(drawer, Array.from(freshDrawer.childNodes));
      }
    } catch (err) {
      // Last-resort fallback
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  }

  /* -- Core sync -- */

  async function syncShippingProperties() {
    if (_isSyncing) { _pendingSync = true; return; }

    // Never mutate line keys while the customer's own quantity/remove request is
    // in flight: that request carries the key we would be invalidating.
    if (_userBusy) { _pendingSync = true; setTimeout(syncShippingProperties, 400); return; }

    _isSyncing   = true;
    _pendingSync = false;

    var didUpdate = false;

    try {
      var cart = await (await fetch(Shopify.routes.root + 'cart.js')).json();

      // Work through the cart by KEY, not by index. A property patch re-keys the
      // line and can move it, so an index-based loop skips or repeats items.
      var queue = cart.items.map(function (item) { return item.key; });
      var used  = {};

      for (var q = 0; q < queue.length; q++) {
        if (_userBusy) { _pendingSync = true; break; }

        var key  = queue[q];
        var item = null;
        for (var n = 0; n < cart.items.length; n++) {
          if (cart.items[n].key === key) { item = cart.items[n]; break; }
        }
        if (!item || !item.handle) continue;

        var hasDeliveryProp = false;
        if (item.properties) {
          for (var j = 0; j < ALL_DELIVERY_KEYS.length; j++) {
            if (item.properties[ALL_DELIVERY_KEYS[j]]) { hasDeliveryProp = true; break; }
          }
        }

        var shipping   = await fetchVariantShipping(item.handle);
        var mergedProps = {};
        var message;

        if (shipping.hide) {
          // v5 (29 SEP 2026): pre-order product with "Hide pre-order delivery" on. No delivery
          // property at all, and strip one that an earlier add or an older script wrote.
          if (!hasDeliveryProp) continue;
          message = null;
          if (item.properties) {
            for (var h in item.properties) {
              if (ALL_DELIVERY_KEYS.indexOf(h) !== -1) continue;
              mergedProps[h] = item.properties[h];
            }
          }
        } else {
          var customTime = shipping.variants[String(item.variant_id)];
          if (!customTime) continue;

          message = buildShippingMessage(customTime);
          if (!message) continue;

          // Already correct for this language: leave the line, and its key, alone.
          if (hasDeliveryProp && item.properties[PROPERTY_KEY] === message) continue;

          if (item.properties) {
            for (var k in item.properties) {
              if (ALL_DELIVERY_KEYS.indexOf(k) !== -1 && k !== PROPERTY_KEY) continue;
              mergedProps[k] = item.properties[k];
            }
          }
          mergedProps[PROPERTY_KEY] = message;
        }

        var variantId = item.variant_id;

        try {
          var resp = await fetch(Shopify.routes.root + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: mergedProps })
          });

          if (resp.ok) {
            didUpdate = true;
            cart = await resp.json();

            // Find the line we just wrote and hand its new key to the DOM.
            var newKey = null;
            for (var m = 0; m < cart.items.length; m++) {
              var candidate = cart.items[m];
              if (candidate.variant_id !== variantId) continue;
              if (message === null) {
                if (candidate.properties && ALL_DELIVERY_KEYS.some(function (dk) { return candidate.properties[dk]; })) continue;
              } else if (!candidate.properties || candidate.properties[PROPERTY_KEY] !== message) continue;
              if (used[candidate.key]) continue;
              newKey = candidate.key;
              break;
            }

            if (newKey) {
              used[newKey] = true;
              rekeyDom(key, newKey);
              queue[q] = newKey;
            }
          }
        } catch (err) {
          console.warn('[ShippingSync] update failed', item.title, err);
        }
      }
    } catch (err) {
      console.warn('[ShippingSync] sync error', err);
    } finally {
      _isSyncing = false;
    }

    if (didUpdate) {
      syncRemoveLinks();
      await refreshCartDrawer();
      syncRemoveLinks();
    }

    if (_pendingSync) {
      setTimeout(syncShippingProperties, 300);
    }
  }

  /* -- Listeners -- */

  var _timer = null;

  // Pause while a customer-initiated cart change is in flight.
  document.documentElement.addEventListener('line-item:will-change', function () {
    _userBusy = true;
  }, true);

  function releaseUser() { _userBusy = false; }
  document.documentElement.addEventListener('line-item:change', releaseUser, true);
  document.documentElement.addEventListener('line-item:error', releaseUser, true);

  // Debounced: wait for rapid-fire bundle adds to settle
  document.addEventListener('cart:change', function () {
    // Don't re-trigger from our own refreshCartDrawer
    clearTimeout(_timer);
    _timer = setTimeout(syncShippingProperties, 800);
  });

  // Also catch the variant:add event directly (fires before cart:change)
  document.addEventListener('variant:add', function () {
    clearTimeout(_timer);
    _timer = setTimeout(syncShippingProperties, 800);
  });

  // Initial page-load sync
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(syncShippingProperties, 1500); });
  } else {
    setTimeout(syncShippingProperties, 1500);
  }
})();
